// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Users,
  CheckCircle2,
  Clock,
  Link2,
  Activity,
  ArrowRight,
  Brain,
  Bell,
  FileText,
  Send,
  UserPlus,
  Building2,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  ListChecks,
  UserX,
  ClipboardCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiFetch } from '@/lib/api'
import { formatLongDisplayDate } from '@/lib/date-format'
import { useAuth } from '@/context/auth-context'

// ── Date helpers ────────────────────────────────────────────────────────────

function getGreeting(t: (key: string) => string) {
  const h = new Date().getHours()
  if (h < 12) return t('dashboard.greetingMorning')
  if (h < 17) return t('dashboard.greetingAfternoon')
  return t('dashboard.greetingEvening')
}

function formatFullDate() {
  return formatLongDisplayDate()
}

const CLINIC_TZ = 'America/New_York'

function startOfClinicDay(d: Date): Date {
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  const [y, m, day] = ymd.split('-').map(Number) as [number, number, number]
  const guess = new Date(Date.UTC(y, m - 1, day, 0, 0, 0))
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: CLINIC_TZ, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(guess)
  const g = (t: string) => Number(parts.find((p) => p.type === t)!.value)
  const asLocal = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'))
  return new Date(guess.getTime() - (asLocal - guess.getTime()))
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

function formatEmailName(email?: string | null): string {
  const localPart = email?.split('@')[0]?.trim()
  if (!localPart) return ''
  return localPart
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function getUserGreetingName(user: {
  displayName?: string | null
  email?: string | null
  providerData?: Array<{ displayName?: string | null }>
} | null): string {
  const displayName = user?.displayName?.trim()
  if (displayName) return displayName
  const providerName = user?.providerData
    ?.map((profile) => profile.displayName?.trim())
    .find(Boolean)
  if (providerName) return providerName
  return formatEmailName(user?.email)
}

// ── Dashboard summary (GET /v1/dashboard/summary, org-scoped) ─────────────────

interface DashboardSummary {
  totalPatients: number
  prepStatus: { unopened: number; inProgress: number; completed: number; reviewed: number }
  completionRate: number
  activeClinics: number
}

interface DashboardAppointment {
  status?: string
  intakeStatus?: string
}

interface AppointmentsResponse {
  appointments?: DashboardAppointment[]
  total?: number
}

interface FrontDeskStats {
  checkedIn: number
  inQueue: number
  noShows: number
  intakeComplete: number
  readyForProvider: number
  intakeInProgress: number
  notStarted: number
}

const EMPTY_FRONT_DESK_STATS: FrontDeskStats = {
  checkedIn: 0,
  inQueue: 0,
  noShows: 0,
  intakeComplete: 0,
  readyForProvider: 0,
  intakeInProgress: 0,
  notStarted: 0,
}

const FRONT_DESK_APPOINTMENTS_PAGE_SIZE = 100

function statusKey(status?: string): string {
  return (status ?? '').toLowerCase().replace(/-/g, '_').trim()
}

function intakeKey(status?: string): string {
  return (status ?? '').toLowerCase().replace(/-/g, '_').trim()
}

function computeFrontDeskStats(appointments: DashboardAppointment[]): FrontDeskStats {
  return appointments.reduce<FrontDeskStats>((acc, appointment) => {
    const apptStatus = statusKey(appointment.status)
    const currentIntake = intakeKey(appointment.intakeStatus)
    if (apptStatus === 'checked_in') acc.checkedIn += 1
    if (
      ['proposed', 'pending', 'booked', 'arrived', 'checked_in', 'waitlist'].includes(apptStatus) &&
      !['prep_ready', 'in_progress', 'reviewed'].includes(currentIntake)
    ) {
      acc.inQueue += 1
    }
    if (apptStatus === 'noshow') acc.noShows += 1
    if (currentIntake === 'prep_ready') acc.readyForProvider += 1
    if (currentIntake === 'in_progress') acc.intakeInProgress += 1
    if (currentIntake === 'not_sent' || currentIntake === 'link_sent') acc.notStarted += 1
    if (currentIntake === 'prep_ready' || currentIntake === 'reviewed') acc.intakeComplete += 1
    return acc
  }, { ...EMPTY_FRONT_DESK_STATS })
}

// ── Recent activity (GET /v1/activity, org-scoped) ───────────────────────────

type ActivityType =
  | 'prep_note_submitted'
  | 'link_sent'
  | 'link_opened'
  | 'ai_summary_generated'
  | 'patient_added'
  | 'appointment_completed'

interface ApiActivity {
  id: string
  type: ActivityType
  patientId?: string
  patientName: string
  actor?: string
  timestamp: string // ISO 8601 — client renders the relative time
}

// Presentation is owned by the client; the API only sends a semantic `type`.
const ACTIVITY_META: Record<
  ActivityType,
  { icon: React.ElementType; color: string; bg: string; textKey: string }
> = {
  prep_note_submitted:   { icon: FileText,     color: 'text-emerald-500', bg: 'bg-emerald-500/10', textKey: 'dashboard.activityPrepNoteSubmitted'   },
  link_sent:             { icon: Send,         color: 'text-[#006EF9]',   bg: 'bg-[#006EF9]/10',  textKey: 'dashboard.activityLinkSent'            },
  link_opened:           { icon: Bell,         color: 'text-amber-500',   bg: 'bg-amber-500/10',  textKey: 'dashboard.activityLinkOpened'          },
  ai_summary_generated:  { icon: Brain,        color: 'text-[#02C7C3]',   bg: 'bg-[#02C7C3]/10',  textKey: 'dashboard.activityAiSummary'           },
  patient_added:         { icon: UserPlus,     color: 'text-purple-500',  bg: 'bg-purple-500/10', textKey: 'dashboard.activityPatientAdded'        },
  appointment_completed: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10', textKey: 'dashboard.activityAppointmentCompleted' },
}

// ISO timestamp → localized relative string ("12 min ago").
function formatRelativeTime(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffMin = Math.max(0, Math.floor((Date.now() - then) / 60000))
  if (diffMin < 1) return t('dashboard.justNow')
  if (diffMin < 60) return t('dashboard.minAgo', { count: diffMin })
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return t('dashboard.hrAgo', { count: diffHr })
  return t('dashboard.dayAgo', { count: Math.floor(diffHr / 24) })
}

// ── Pagination ───────────────────────────────────────────────────────────────

const ACTIVITY_PAGE_SIZE = 6
const ACTIVITY_SKELETON_ROWS = 4
const DASHBOARD_REFRESH_MS = 30_000

function useLiveRefreshKey(enabled: boolean) {
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!enabled) return
    const refresh = () => setRefreshKey(k => k + 1)
    const interval = window.setInterval(refresh, DASHBOARD_REFRESH_MS)
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }

    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [enabled])

  return refreshKey
}

function Paginator({ page, total, pageSize, onPrev, onNext }: {
  page: number; total: number; pageSize: number; onPrev: () => void; onNext: () => void
}) {
  const { t } = useTranslation()
  const totalPages = Math.ceil(total / pageSize)
  if (totalPages <= 1) return null
  return (
    <div className="flex items-center justify-between px-5 py-3 border-t border-border">
      <button
        onClick={onPrev}
        disabled={page === 0}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground disabled:opacity-30 hover:text-foreground transition-colors disabled:cursor-default"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {t('dashboard.prev')}
      </button>
      <span className="text-xs text-muted-foreground">
        {page + 1} / {totalPages}
      </span>
      <button
        onClick={onNext}
        disabled={page >= totalPages - 1}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground disabled:opacity-30 hover:text-foreground transition-colors disabled:cursor-default"
      >
        {t('dashboard.next')}
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({
  icon: Icon,
  value,
  label,
  sub,
  loading = false,
  accent = false,
  teal = false,
}: {
  icon: React.ElementType
  value: string
  label: string
  sub?: string
  loading?: boolean
  accent?: boolean
  teal?: boolean
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0d52a0] via-[#1a70d8] to-[#0ea5e9] dark:from-[#012031] dark:via-[#033C85] dark:to-[#006EF9] p-4 lg:p-5 h-full min-h-[86px] flex items-center gap-3 lg:gap-4">
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/5 blur-xl pointer-events-none" />
      <div className={cn(
        'h-12 w-12 rounded-xl flex items-center justify-center shrink-0',
        teal ? 'bg-[#02C7C3]/25' : accent ? 'bg-white/20' : 'bg-white/15',
      )}>
        <Icon className={cn('h-6 w-6', teal ? 'text-[#02C7C3]' : 'text-white')} />
      </div>
      <div className="min-w-0">
        {loading ? (
          <div className="h-7 w-12 rounded shimmer bg-white/20" />
        ) : (
          <p className="text-2xl font-bold text-white leading-none">{value}</p>
        )}
        <p className="text-white/65 text-xs mt-1 leading-snug">{label}</p>
        {sub && <p className="text-white/40 text-[10px] mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

function SecondaryKpiCard({
  icon: Icon,
  value,
  label,
  sub,
  color,
  loading = false,
}: {
  icon: React.ElementType
  value: string
  label: string
  sub?: string
  color: string
  loading?: boolean
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-3.5 lg:p-4 h-full min-h-[72px] flex items-center gap-3 lg:gap-4">
      <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0', color + '/10')}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div>
        {loading ? (
          <div className="h-5 w-10 rounded shimmer" />
        ) : (
          <p className="text-xl font-bold text-foreground leading-none">{value}</p>
        )}
        {sub && <p className="text-[11px] font-medium text-muted-foreground mt-0.5">{sub}</p>}
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </div>
  )
}

function ActivityTableRow({ item }: { item: ApiActivity }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const meta = ACTIVITY_META[item.type]
  // Unknown/forward-compat event types degrade gracefully.
  const Icon = meta?.icon ?? Activity
  const text = meta ? t(meta.textKey, { name: item.patientName }) : item.patientName
  const clickable = Boolean(item.patientId)

  const openPatient = clickable ? () => navigate(`/patients/${item.patientId}`) : undefined

  return (
    <tr
      onClick={openPatient}
      className={cn(
        'border-b border-border last:border-0 transition-colors',
        clickable && 'cursor-pointer hover:bg-secondary/40',
      )}
    >
      <td className="px-5 py-3 min-w-[280px]">
        <div className="flex items-center gap-3">
          <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center shrink-0', meta?.bg ?? 'bg-secondary')}>
            <Icon className={cn('h-4 w-4', meta?.color ?? 'text-muted-foreground')} />
          </div>
          <span className="text-sm text-foreground leading-snug">{text}</span>
        </div>
      </td>
      <td className="px-5 py-3 min-w-[160px] text-sm font-medium text-foreground">{item.patientName || '—'}</td>
      <td className="px-5 py-3 min-w-[120px] text-sm text-muted-foreground whitespace-nowrap">{formatRelativeTime(item.timestamp, t) || '—'}</td>
      <td className="px-5 py-3 min-w-[140px] text-sm text-muted-foreground">{item.actor || '—'}</td>
    </tr>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, actingAsOrg, isOperator } = useAuth()
  // Org id keys every data fetch so switching orgs (operator) refreshes the
  // whole dashboard consistently — same X-Org-Context the calls already send.
  const orgKey = actingAsOrg?.id ?? ''
  // An operator with no org selected has no data to show — prompt them to pick
  // one and skip the org-scoped fetches (which 404 / return empty without a
  // context). RequireAuth guarantees role is resolved before this renders, so
  // isOperator is accurate on first render.
  const needsOrgSelection = isOperator && !actingAsOrg
  const liveRefreshKey = useLiveRefreshKey(!needsOrgSelection)

  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [summaryStatus, setSummaryStatus] = useState<'loading' | 'done' | 'error'>('loading')
  const [frontDeskStats, setFrontDeskStats] = useState<FrontDeskStats>(EMPTY_FRONT_DESK_STATS)
  const [frontDeskStatus, setFrontDeskStatus] = useState<'loading' | 'done' | 'error'>('loading')

  const [activity, setActivity] = useState<ApiActivity[]>([])
  const [activityTotal, setActivityTotal] = useState(0)
  const [activityPage, setActivityPage] = useState(0)
  const [activityStatus, setActivityStatus] = useState<'loading' | 'done' | 'error'>('loading')

  const displayName = getUserGreetingName(user)

  // Summary KPIs.
  useEffect(() => {
    if (needsOrgSelection) return
    const controller = new AbortController()
    setSummaryStatus((current) => current === 'done' ? 'done' : 'loading')
    ;(async () => {
      try {
        const res = await apiFetch('/v1/dashboard/summary', { signal: controller.signal })
        if (!res.ok) throw new Error(`Server returned ${res.status}`)
        setSummary((await res.json()) as DashboardSummary)
        setSummaryStatus('done')
      } catch {
        if (controller.signal.aborted) return
        setSummaryStatus((current) => current === 'done' ? 'done' : 'error')
      }
    })()
    return () => controller.abort()
  }, [orgKey, needsOrgSelection, liveRefreshKey])

  // Today's front-desk counts are derived from the same appointment worklist data
  // the schedule board uses: appointment status + intake status.
  useEffect(() => {
    if (needsOrgSelection) return
    const controller = new AbortController()
    setFrontDeskStatus((current) => current === 'done' ? 'done' : 'loading')
    ;(async () => {
      try {
        const from = startOfClinicDay(new Date())
        const to = addDays(from, 1)
        const appointments: DashboardAppointment[] = []
        let offset = 0
        let total = 0
        do {
          const params = new URLSearchParams({
            from: from.toISOString(),
            to: to.toISOString(),
            limit: String(FRONT_DESK_APPOINTMENTS_PAGE_SIZE),
            offset: String(offset),
          })
          const res = await apiFetch(`/v1/appointments?${params.toString()}`, { signal: controller.signal })
          if (!res.ok) throw new Error(`Server returned ${res.status}`)
          const data = (await res.json()) as AppointmentsResponse
          const page = data.appointments ?? []
          appointments.push(...page)
          total = data.total ?? appointments.length
          offset += FRONT_DESK_APPOINTMENTS_PAGE_SIZE
          if (page.length === 0) break
        } while (appointments.length < total)
        setFrontDeskStats(computeFrontDeskStats(appointments))
        setFrontDeskStatus('done')
      } catch {
        if (controller.signal.aborted) return
        setFrontDeskStatus((current) => current === 'done' ? 'done' : 'error')
      }
    })()
    return () => controller.abort()
  }, [orgKey, needsOrgSelection, liveRefreshKey])

  // Recent activity — real, org-scoped, act-as-aware audit feed (server-paginated).
  useEffect(() => {
    if (needsOrgSelection) return
    const controller = new AbortController()
    setActivityStatus((current) => current === 'done' ? 'done' : 'loading')
    const offset = activityPage * ACTIVITY_PAGE_SIZE
    ;(async () => {
      try {
        const res = await apiFetch(`/v1/activity?limit=${ACTIVITY_PAGE_SIZE}&offset=${offset}`, {
          signal: controller.signal,
        })
        if (!res.ok) throw new Error(`Server returned ${res.status}`)
        const data = (await res.json()) as {
          activity?: ApiActivity[]
          pagination?: { total?: number }
        }
        const items = data.activity ?? []
        setActivity(items)
        setActivityTotal(data.pagination?.total ?? items.length)
        setActivityStatus('done')
      } catch {
        if (controller.signal.aborted) return
        setActivityStatus((current) => current === 'done' ? 'done' : 'error')
      }
    })()
    return () => controller.abort()
  }, [orgKey, needsOrgSelection, activityPage, liveRefreshKey])

  // Reset to the first activity page whenever the org changes.
  useEffect(() => {
    setActivityPage(0)
  }, [orgKey])

  const loadingKpis = summaryStatus === 'loading'
  const loadingFrontDeskStats = frontDeskStatus === 'loading'

  return (
    <div className="flex h-[calc(100svh-5.5rem)] min-h-0 flex-col gap-4 overflow-hidden lg:h-[calc(100svh-7.5rem)] lg:gap-6">

      {/* ── Header ── */}
      <div className="flex shrink-0 flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-[#02C7C3] uppercase tracking-widest mb-1">{formatFullDate()}</p>
          <h1 className="text-2xl font-bold text-foreground">
            {getGreeting(t)}{displayName ? `, ${displayName}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('dashboard.overviewSubtitle')}</p>
        </div>
      </div>

      {needsOrgSelection ? (
        /* Operator with no org selected — prompt rather than 404 / fake zeros. */
        <div className="bg-card border border-border rounded-2xl p-10 flex flex-col items-center text-center gap-3 animate-fade-up">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">{t('dashboard.selectOrgTitle')}</h2>
          <p className="text-sm text-muted-foreground max-w-sm">{t('dashboard.selectOrgBody')}</p>
        </div>
      ) : (
       <>
      {/* ── Primary KPI row ── */}
      <div className="grid shrink-0 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 stagger-children">
        <div className="animate-fade-up h-full">
          <KpiCard icon={UserCheck} loading={loadingFrontDeskStats} value={String(frontDeskStats.checkedIn)} label={t('dashboard.checkedInToday')} />
        </div>
        <div className="animate-fade-up h-full">
          <KpiCard icon={ListChecks} loading={loadingFrontDeskStats} value={String(frontDeskStats.inQueue)} label={t('dashboard.inQueueToday')} teal />
        </div>
        <div className="animate-fade-up h-full">
          <KpiCard icon={UserX} loading={loadingFrontDeskStats} value={String(frontDeskStats.noShows)} label={t('dashboard.noShowsToday')} accent />
        </div>
        <div className="animate-fade-up h-full">
          <KpiCard icon={ClipboardCheck} loading={loadingFrontDeskStats} value={String(frontDeskStats.intakeComplete)} label={t('dashboard.intakeCompleteToday')} teal />
        </div>
      </div>

      {/* ── Secondary KPI row ── */}
      <div className="grid shrink-0 grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 stagger-children">
        <div className="animate-fade-up h-full">
          <SecondaryKpiCard icon={CheckCircle2} loading={loadingFrontDeskStats} value={String(frontDeskStats.readyForProvider)} label={t('dashboard.prepNotesCompleted')} color="text-emerald-500" />
        </div>
        <div className="animate-fade-up h-full">
          <SecondaryKpiCard icon={Activity} loading={loadingFrontDeskStats} value={String(frontDeskStats.intakeInProgress)} label={t('dashboard.inProgress')} color="text-[#006EF9]" />
        </div>
        <div className="animate-fade-up h-full">
          <SecondaryKpiCard icon={Clock} loading={loadingFrontDeskStats} value={String(frontDeskStats.notStarted)} label={t('dashboard.linksUnopened')} color="text-amber-500" />
        </div>
        <div className="animate-fade-up h-full">
          <SecondaryKpiCard icon={Building2} loading={loadingKpis} value={String(summary?.activeClinics ?? 0)} label={t('dashboard.activeClinics')} color="text-[#02C7C3]" />
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div className="flex shrink-0 flex-wrap gap-3">
        <button
          onClick={() => navigate('/patients')}
          className="flex items-center gap-2 h-9 px-4 rounded-xl border border-border bg-card text-sm font-medium text-foreground hover:bg-secondary transition-colors"
        >
          <Users className="h-4 w-4 text-muted-foreground" />
          {t('dashboard.viewAllPatients')}
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
        <button
          onClick={() => navigate('/patients')}
          className="flex items-center gap-2 h-9 px-4 rounded-xl border border-border bg-card text-sm font-medium text-foreground hover:bg-secondary transition-colors"
        >
          <Link2 className="h-4 w-4 text-muted-foreground" />
          {t('dashboard.sendPrepLinks')}
          {frontDeskStats.notStarted > 0 && (
            <span className="ml-1 text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5">
              {frontDeskStats.notStarted}
            </span>
          )}
        </button>
      </div>

      {/* ── Recent Activity ── */}
      <div className="min-h-0 flex-1 bg-card border border-border rounded-2xl overflow-hidden animate-fade-up flex flex-col" style={{ animationDelay: '200ms' }}>
        <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">{t('dashboard.recentActivity')}</p>
        </div>
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
          <table className="w-full text-left">
            <thead className="bg-secondary/50 border-b border-border">
              <tr>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('dashboard.activityColActivity')}
                </th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('dashboard.activityColPatient')}
                </th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('dashboard.activityColTime')}
                </th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('dashboard.activityColActor')}
                </th>
              </tr>
            </thead>
            <tbody>
              {activityStatus === 'loading' ? (
                Array.from({ length: ACTIVITY_SKELETON_ROWS }).map((_, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 min-w-[280px]">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg shimmer shrink-0" />
                        <div className="h-3.5 w-48 rounded shimmer" />
                      </div>
                    </td>
                    <td className="px-5 py-3 min-w-[160px]">
                      <div className="h-3.5 w-24 rounded shimmer" />
                    </td>
                    <td className="px-5 py-3 min-w-[120px]">
                      <div className="h-3.5 w-20 rounded shimmer" />
                    </td>
                    <td className="px-5 py-3 min-w-[140px]">
                      <div className="h-3.5 w-24 rounded shimmer" />
                    </td>
                  </tr>
                ))
              ) : activityStatus === 'error' ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    {t('dashboard.activityError')}
                  </td>
                </tr>
              ) : activity.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    {t('dashboard.activityEmpty')}
                  </td>
                </tr>
              ) : (
                activity.map((item) => <ActivityTableRow key={item.id} item={item} />)
              )}
            </tbody>
          </table>
        </div>
        <Paginator
          page={activityPage}
          total={activityTotal}
          pageSize={ACTIVITY_PAGE_SIZE}
          onPrev={() => setActivityPage(p => p - 1)}
          onNext={() => setActivityPage(p => p + 1)}
        />
      </div>
       </>
      )}

    </div>
  )
}
